// ────────────────────────────────────────────────────────────────────────────
// Turning an order into a journey plan: which stops, which crossing, and where
// the ports are.
//
// Kept apart from `journeyPlan.ts` so the engine stays pure and testable while
// all the "where does this data actually come from" mess lives here.
// ────────────────────────────────────────────────────────────────────────────
import {
  roadLegEnd,
  type Address,
  type CrossingPreferenceValue,
  type RoadLegQuery,
  type RouteCatalogueEntry,
} from '@my/directory-api';
import { portCoordinatesByName } from '../../../utils/portCoordinates';
import type { OrderBooking } from '@my/tms-api';
import {
  buildJourneyPlan,
  detectCountryChange,
  estimatedRoadKm,
  roadMinutesFor,
  type JourneyPlan,
  type PlanCrossing,
  type PlanStop,
  type RoadLeg,
  type RoadLegResolver,
} from './journeyPlan';
import { CARGO_HANDLING_TASK_TYPES } from './cargoPick';
import { isCustomsCrossing, toLandmass } from '../../bookings/lib/crossing';
import type { MockTask } from './types';

/**
 * Coordinates for a named port.
 *
 * <p>
 * Prefers one of the org's OWN geocoded addresses whose name matches, because
 * that is the real terminal; falls back to the shared port table for a port
 * nobody has geocoded yet. Only reached when a booking half's address has no
 * coordinates — see `toCrossing`.
 * </p>
 */
export function portCoordinates(
  portName: string | null | undefined,
  addresses: Address[],
): PortPoint | null {
  if (!portName) return null;
  const needle = portName.trim().toLowerCase();
  if (!needle) return null;

  const own = addresses.find((a) => {
    if (a.latitude == null || a.longitude == null) return false;
    const name = a.name?.trim().toLowerCase();
    // An unnamed address never matches. The previous fallback substituted a
    // placeholder string for a missing name, which made every port name
    // CONTAINING that character a match — and one of those placeholders had
    // been mangled into a stray NUL byte in the source.
    if (!name) return false;
    return name.includes(needle) || needle.includes(name);
  });
  if (own) {
    const end = roadLegEnd(own);
    if (end) return end;
  }

  return portCoordinatesByName(portName);
}

/** The stops that make up the journey — cargo-handling only. */
function toStops(tasks: MockTask[], addressById: Map<string, Address>): PlanStop[] {
  return tasks
    .filter((t) => {
      const src = t._source;
      // Booking halves are the crossing, not stops on the road, and process
      // events (Stop / Instruction / Inspection) carry no cargo. Including
      // either would double-count the ferry or invent a leg.
      return src != null && src.state !== 'Cancelled' && CARGO_HANDLING_TASK_TYPES.has(src.type);
    })
    .map((t) => {
      const address = t._source?.addressId ? addressById.get(t._source.addressId) : undefined;
      const end = address ? roadLegEnd(address) : null;
      return {
        label: t.title,
        countryIsoCode: address?.countryIsoCode ?? t.countryCode ?? null,
        latitude: address?.latitude ?? null,
        longitude: address?.longitude ?? null,
        placeId: end?.placeId ?? null,
        addressId: end?.addressId ?? null,
        plannedDate: t._source?.plannedDate ?? null,
        plannedTime: t._source?.plannedTime ?? null,
        timeMode: t._source?.timeMode === 'Any' ? 'Undefined' : (t._source?.timeMode ?? null),
      } satisfies PlanStop;
    });
}

/**
 * The crossing, from a booking on the order.
 *
 * <p>
 * The port NAMES and coordinates come from the booking's own paired tasks, not
 * from the booking row (which carries only a route name) and not from a lookup
 * table. The Boarding half sits at the departure port and the Disembark half at
 * the arrival port, both as ordinary geocoded addresses in the org's own book —
 * so they are exact, already maintained, and need nothing set up.
 * </p>
 */
function toCrossing(
  booking: OrderBooking | undefined,
  tasks: MockTask[],
  addressById: Map<string, Address>,
): { crossing: PlanCrossing; ports: { origin: PortPoint | null; destination: PortPoint | null } } | null {
  if (!booking) return null;

  const half = (type: 'BookingDeparture' | 'BookingArrival') => {
    const task = tasks.find((t) => t._source?.type === type && t._source?.bookingId === booking.id);
    const address = task?._source?.addressId ? addressById.get(task._source.addressId) : undefined;
    return { task, address };
  };

  const departure = half('BookingDeparture');
  const arrival = half('BookingArrival');

  const point = (address: Address | undefined): PortPoint | null => (address ? roadLegEnd(address) : null);

  const originName = departure.address?.name ?? departure.task?.title ?? 'Departure port';
  const destinationName = arrival.address?.name ?? arrival.task?.title ?? 'Arrival port';

  return {
    crossing: {
      routeName: booking.routeName ?? 'Sea crossing',
      originPortName: originName,
      destinationPortName: destinationName,
      crossingMinutes: booking.typicalDurationMinutes ?? 0,
      checkInMinutes: booking.typicalCheckInMinutes ?? 0,
      driveOffMinutes: booking.typicalDriveOffMinutes ?? 0,
      sailDate: booking.sailDate ?? null,
      sailTime: booking.sailTime ?? null,
      customs: !!booking.customsReference || booking.clearanceStatus != null,
    },
    ports: {
      // The table is the fallback for a port address nobody has geocoded yet.
      origin: point(departure.address) ?? portCoordinates(originName, []),
      destination: point(arrival.address) ?? portCoordinates(destinationName, []),
    },
  };
}

/**
 * A port's position. From one of the org's own addresses it carries that
 * address's place id (when Google-sourced) and id; from the built-in port table
 * it is coordinates only — our own data.
 */
type PortPoint = {
  latitude: number;
  longitude: number;
  placeId?: string | null;
  addressId?: string | null;
};

/** A crossing plus the coordinates of its two ports. */
interface ResolvedCrossing {
  crossing: PlanCrossing;
  ports: { origin: PortPoint | null; destination: PortPoint | null };
}

/**
 * The best candidate crossing for a country change nobody has booked yet.
 *
 * <p>
 * Scored on TOTAL transit — the road either side plus check-in, the sailing and
 * drive-off. Scoring the road alone would prefer a short drive onto a long boat,
 * and the two are the same hours to a driver. For a Cork collection the score
 * picks Rosslare–Fishguard into south Wales and Dublin–Holyhead into Chester,
 * which is what an operator would do. Routes are catalogued in one canonical
 * direction but both legs are operated, so each entry is considered reversed as
 * well, with its port names swapped.
 * </p>
 */
/** Whether a catalogue route links these two landmasses, in either direction. */
function serves(entry: RouteCatalogueEntry, a: string | null, b: string | null): boolean {
  const origin = toLandmass(entry.originCustomsTerritory ?? entry.originCountryIsoCode);
  const destination = toLandmass(entry.destinationCustomsTerritory ?? entry.destinationCountryIsoCode);
  return (origin === a && destination === b) || (origin === b && destination === a);
}

/**
 * What an hour on the road weighs against an hour at sea when ranking
 * crossings, per the organisation's "Suggest crossings by" choice. Cheapest
 * has no rates to rank on yet (the server refuses it), so it ranks as Fastest.
 */
const ROAD_WEIGHT: Record<CrossingPreferenceValue, number> = {
  Fastest: 1,
  Balanced: 1.5,
  LessDriving: 2,
  Cheapest: 1,
};

function pickCandidateCrossing(args: {
  countryChange: { from: string; to: string };
  first: PlanStop;
  last: PlanStop;
  routes: readonly RouteCatalogueEntry[];
  /** Multiplier on the road either side; 1 = minutes are minutes. */
  roadWeight: number;
}): ResolvedCrossing | null {
  const { from, to } = args.countryChange;

  // Matched on LANDMASS, not country: NL→XI has no direct sailing, but
  // Rotterdam→Dublin and a drive north does it, and Belfast→Cairnryan serves
  // XI even though Belfast's official country is GB — its customs territory
  // (XI) is what puts it on the island of Ireland.
  const fromLand = toLandmass(from);
  const toLand = toLandmass(to);
  // This plans a DRIVER's journey, so a trailers-only service is a last
  // resort: offered only when nothing the truck can sail on serves the pair.
  const sailable = args.routes.filter((r) => r.accompaniment !== 'unaccompanied');
  const pool = sailable.some((r) => serves(r, fromLand, toLand)) ? sailable : args.routes;
  const legs = pool.flatMap((entry) => {
    const origin = toLandmass(entry.originCustomsTerritory ?? entry.originCountryIsoCode);
    const destination = toLandmass(entry.destinationCustomsTerritory ?? entry.destinationCountryIsoCode);
    if (origin === fromLand && destination === toLand) return [{ entry, reversed: false }];
    if (origin === toLand && destination === fromLand) return [{ entry, reversed: true }];
    return [];
  });
  if (legs.length === 0) return null;

  // A port with no coordinates cannot be scored, so it sorts last rather than
  // winning by scoring zero.
  const UNSCORED = Number.MAX_SAFE_INTEGER;
  const roadMinutes = (
    a: { latitude?: number | null; longitude?: number | null } | null,
    b: { latitude?: number | null; longitude?: number | null } | null,
  ): number | null => {
    if (!a || !b) return null;
    const km = estimatedRoadKm(a, b);
    return km == null ? null : roadMinutesFor(km);
  };

  const scored = legs.map((leg) => {
    const originName = leg.reversed
      ? leg.entry.destinationPortName
      : leg.entry.originPortName;
    const destinationName = leg.reversed
      ? leg.entry.originPortName
      : leg.entry.destinationPortName;
    const originPort = portCoordinates(originName, []);
    const destinationPort = portCoordinates(destinationName, []);
    const inbound = roadMinutes(args.first, originPort);
    const outbound = roadMinutes(destinationPort, args.last);
    return {
      leg,
      originName,
      destinationName,
      originPort,
      destinationPort,
      score:
        inbound == null || outbound == null
          ? UNSCORED
          : (inbound + outbound) * args.roadWeight +
            (leg.entry.typicalCheckInMinutes ?? 0) +
            (leg.entry.typicalDurationMinutes ?? 0) +
            (leg.entry.typicalDriveOffMinutes ?? 0),
    };
  });

  scored.sort((a, b) => a.score - b.score);
  const best = scored[0]!;
  const entry = best.leg.entry;

  return {
    crossing: {
      routeName: `${best.originName} to ${best.destinationName}`,
      originPortName: best.originName,
      destinationPortName: best.destinationName,
      crossingMinutes: entry.typicalDurationMinutes ?? 0,
      checkInMinutes: entry.typicalCheckInMinutes ?? 0,
      driveOffMinutes: entry.typicalDriveOffMinutes ?? 0,
      sailDate: null,
      sailTime: null,
      // The goods' from / to (the stops either side), not the ports: NL→XI
      // via Dublin is EU→XI, no customs; GB→XI via Cairnryan is.
      customs: isCustomsCrossing(from, to) ?? false,
      provisional: true,
    },
    ports: { origin: best.originPort, destination: best.destinationPort },
  };
}

/**
 * A crossing with no route behind it at all — the country changes, so a boat is
 * needed, but nothing in the catalogue serves that pair.
 *
 * <p>
 * Worth showing anyway. The alternative is what this panel did before: a single
 * road leg straight across the Irish Sea and a confident ETA derived from it.
 * Two unnamed ports and no times is the honest version of the same picture.
 * </p>
 */
function unknownCrossing(countryChange: { from: string; to: string }): ResolvedCrossing {
  return {
    crossing: {
      routeName: 'Sea crossing',
      originPortName: `Port (${countryChange.from})`,
      destinationPortName: `Port (${countryChange.to})`,
      crossingMinutes: 0,
      checkInMinutes: 0,
      driveOffMinutes: 0,
      sailDate: null,
      sailTime: null,
      customs: isCustomsCrossing(countryChange.from, countryChange.to) ?? false,
      provisional: true,
    },
    ports: { origin: null, destination: null },
  };
}

/**
 * One road leg this journey needs a real driving time for, in the shape the
 * routing endpoint takes so it can be passed straight through.
 */
export type OrderRoadPair = RoadLegQuery;

export interface OrderJourneyResult {
  plan: JourneyPlan;
  booked: boolean;
  routeName: string | null;
  /**
   * The road legs to route, in the order the plan uses them.
   *
   * <p>
   * Returned rather than requested, because only this adapter knows which legs
   * a journey HAS — collection to the port and the port to the delivery for a
   * crossing, one direct leg without. The caller fetches these and builds the
   * plan a second time with real times, which is cheap: the engine is pure.
   * </p>
   */
  legPairs: OrderRoadPair[];
}

/**
 * Cache key for a leg, rounded to about 100 m.
 *
 * <p>
 * The same rounding the server caches on, so both layers agree about what
 * counts as the same leg — two collections at one industrial estate should not
 * be two different legs.
 * </p>
 */
function roadPairKey(
  from: { latitude?: number | null; longitude?: number | null },
  to: { latitude?: number | null; longitude?: number | null },
): string {
  const at = (value?: number | null) => (value ?? 0).toFixed(3);
  return `${at(from.latitude)},${at(from.longitude)}>${at(to.latitude)},${at(to.longitude)}`;
}

/**
 * A resolver over routed legs, for the second pass.
 *
 * <p>
 * Legs the service could not route come back with null times; those keys are
 * left out of the map, so `buildJourneyPlan` falls back to its straight-line
 * estimate for that leg alone and still marks it with a "~". A partial answer
 * is the normal case, not an error.
 * </p>
 */
export function makeRoadResolver(
  pairs: OrderRoadPair[],
  routed: { index: number; minutes: number | null; kilometres: number | null }[],
): RoadLegResolver {
  const byKey = new Map<string, RoadLeg>();
  for (const leg of routed) {
    const pair = pairs[leg.index];
    if (!pair || leg.minutes == null) continue;
    byKey.set(
      roadPairKey(
        { latitude: pair.fromLatitude, longitude: pair.fromLongitude },
        { latitude: pair.toLatitude, longitude: pair.toLongitude },
      ),
      { minutes: leg.minutes, km: leg.kilometres ?? undefined, estimated: false },
    );
  }
  return (from, to) => byKey.get(roadPairKey(from, to)) ?? null;
}

/** The pairs a journey of this shape needs, skipping anything ungeocoded. */
function toRoadPairs(
  stops: PlanStop[],
  ports: { origin: PortPoint | null; destination: PortPoint | null } | null,
): OrderRoadPair[] {
  const first = stops[0];
  const last = stops[stops.length - 1];
  type End = {
    latitude?: number | null;
    longitude?: number | null;
    placeId?: string | null;
    addressId?: string | null;
  };
  const pair = (a: End | null | undefined, b: End | null | undefined): OrderRoadPair | null =>
    a?.latitude != null && a.longitude != null && b?.latitude != null && b.longitude != null
      ? {
          fromLatitude: a.latitude,
          fromLongitude: a.longitude,
          fromPlaceId: a.placeId ?? null,
          fromAddressId: a.addressId ?? null,
          toLatitude: b.latitude,
          toLongitude: b.longitude,
          toPlaceId: b.placeId ?? null,
          toAddressId: b.addressId ?? null,
        }
      : null;

  const wanted = ports
    ? [pair(first, ports.origin), pair(ports.destination, last)]
    : [pair(first, last)];
  return wanted.filter((p): p is OrderRoadPair => p !== null);
}

/**
 * Build the plan for an order, or null when there is no journey to show —
 * fewer than two stops.
 */
export function buildOrderJourney(args: {
  tasks: MockTask[];
  bookings: OrderBooking[];
  addresses: Address[];
  /**
   * Routes the org has opted into. Used only when the order needs a crossing
   * and has no booking, to propose the sailing an operator would take.
   */
  orgRoutes?: readonly RouteCatalogueEntry[];
  /**
   * The org's STARRED routes ("we always go this way") — tried before every
   * other route, whatever the crossing preference would rank first.
   */
  preferredRoutes?: readonly RouteCatalogueEntry[];
  /** The org's "Suggest crossings by" choice; Fastest when absent. */
  crossingPreference?: CrossingPreferenceValue | null;
  /**
   * The whole route catalogue, as a second choice behind `orgRoutes`. Only the
   * port names and typical durations are taken from it, never the carrier — so
   * proposing a pair the org has not opted into suggests a crossing, not a
   * supplier.
   */
  catalogueRoutes?: readonly RouteCatalogueEntry[];
  /**
   * Real truck driving times, from a second pass. Absent on the first pass —
   * which is the one that reports which legs to fetch.
   */
  resolveRoad?: RoadLegResolver;
}): OrderJourneyResult | null {
  const addressById = new Map(args.addresses.map((a) => [a.id, a]));
  const stops = toStops(args.tasks, addressById);
  if (stops.length < 2) return null;

  // One crossing for now: the overwhelming majority of orders have at most one
  // ferry, and a second would need the stops split around both.
  const booking = args.bookings.find((b) => !!b.sailDate) ?? args.bookings[0];

  // A booking is the crossing when there is one. Otherwise a country change is
  // still a crossing — an unbooked one — and it belongs in the timeline, since
  // "which sailing can we still take" is the question the panel exists to
  // answer and it cannot be asked about a journey drawn as one long drive.
  const countryChange = detectCountryChange(stops);
  const resolved =
    toCrossing(booking, args.tasks, addressById) ??
    (countryChange
      ? // Starred routes first, then the org's other routes, then the whole
        // catalogue when none of them serves this pair — an IE–GB-only org
        // still gets Rotterdam→Dublin suggested for an NL load rather than an
        // unnamed "Port (NL)". Within each tier the crossing preference ranks.
        ([args.preferredRoutes, args.orgRoutes, args.catalogueRoutes].reduce<ResolvedCrossing | null>(
          (found, routes) =>
            found ??
            pickCandidateCrossing({
              countryChange,
              first: stops[0]!,
              last: stops[stops.length - 1]!,
              routes: routes ?? [],
              roadWeight: ROAD_WEIGHT[args.crossingPreference ?? 'Fastest'],
            }),
          null,
        ) ?? unknownCrossing(countryChange))
      : null);
  const crossing = resolved?.crossing ?? null;

  // The clock's day zero: the earliest planned date on the order, so every
  // absolute minute below is relative to something the operator can see.
  const baseDate =
    stops
      .map((s) => s.plannedDate)
      .filter((d): d is string => !!d)
      .sort()[0] ?? new Date().toISOString().slice(0, 10);

  const plan = buildJourneyPlan({
    stops,
    crossing,
    baseDate,
    originPort: resolved?.ports.origin ?? null,
    destinationPort: resolved?.ports.destination ?? null,
    resolveRoad: args.resolveRoad,
  });

  return {
    plan,
    booked: !!booking?.sailDate,
    routeName: crossing?.routeName ?? null,
    legPairs: toRoadPairs(stops, crossing ? (resolved?.ports ?? null) : null),
  };
}
